import React, { useRef, useState } from 'react'
import { BookMarked, ArrowUp, ArrowDown, Trash2, ImagePlus, Plus, Mic, Loader2, CheckCircle2 } from 'lucide-react'
import InteractiveContentRenderer from '../../../components/interactive/InteractiveContentRenderer'
import { isAudioableBlock, TTS_VOICES } from '../../../components/interactive/shared/contentBlocks'
import { cardToContentItems } from '../lib/cardToContentItem'
import { narrateSection } from '../api/lessonBuilderApi'
import AudioSourceButtons from '../components/AudioSourceButtons'
import { useLessonBuilder } from '../store/useLessonBuilder'
import QuestionEditor, { isQuestionBlock } from './QuestionEditor'
import { genId } from '../lib/id'
import type { RichCard } from '../types/LessonDoc'
import type { CardDef, CardEditorProps, CardPreviewProps } from './types'

// Lồng tiếng cho MỘT khối text (diễn đạt nội dung, không đọc tiêu đề/câu hỏi). Chọn giọng được.
const BlockAudio: React.FC<{ block: any; cardId: string; blockIndex: number; onAudio: (patch: any) => void }>
  = ({ block, cardId, blockIndex, onAudio }) => {
  const { lesson } = useLessonBuilder()
  const [voice, setVoice] = useState(block.voice || 'Kore')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)

  const run = async () => {
    const text = (block.content || '').trim()
    if (!text) { setMsg('Khối này chưa có nội dung.'); return }
    setBusy(true); setMsg(null)
    try {
      const r = await narrateSection(lesson.id, `${cardId}-b${blockIndex}`, text, voice)
      onAudio({ audioUrl: r.url, voiceScript: r.script, voice })
      setMsg('✅ Đã tạo audio. Nhớ Lưu bài.')
    } catch (e: any) {
      setMsg(`Lỗi: ${e?.message || 'không tạo được audio'}`)
    } finally { setBusy(false) }
  }

  return (
    <div className="mt-1.5 rounded-lg border border-orange-100 bg-orange-50/60 p-2">
      <div className="flex items-center gap-2 flex-wrap">
        <select value={voice} onChange={(e) => setVoice(e.target.value)} disabled={busy}
          className="text-[11px] border border-gray-200 rounded-md px-1.5 py-1 bg-white outline-none">
          {TTS_VOICES.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
        </select>
        <button onClick={run} disabled={busy}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-orange-500 text-white text-[11px] font-bold hover:bg-orange-600 disabled:opacity-50">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Mic size={12} />}
          {block.audioUrl ? 'Tạo lại' : 'Tạo Audio'}
        </button>
        <span className="text-[10px] text-gray-400">~1 điểm AI</span>
        <span className="text-[10px] text-gray-300">hoặc</span>
        <AudioSourceButtons lessonId={lesson.id} sectionId={`${cardId}-b${blockIndex}`} onAudio={onAudio} disabled={busy} />
        {block.audioUrl && !busy && <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600"><CheckCircle2 size={12} /> đã có</span>}
      </div>
      {block.audioUrl && <audio controls preload="none" src={block.audioUrl} className="mt-1.5 w-full h-8" />}
      {msg && <p className="mt-1 text-[10px] text-gray-600">{msg}</p>}
    </div>
  )
}

// Nhãn loại khối + loại khối cho sửa nội dung trực tiếp
const TYPE_LABEL: Record<string, string> = {
  heading: 'Tiêu đề', text: 'Văn bản', formula: 'Công thức', formula_card: 'Thẻ công thức',
  list: 'Danh sách', table: 'Bảng',
  image: 'Ảnh', concept_card: 'Thẻ kiến thức', example: 'Ví dụ', warning: 'Lưu ý', conclusion_card: 'Tổng kết',
  quiz: 'Bộ câu hỏi', interactive_question: 'Câu hỏi', multiple_response_question: 'Câu nhiều đáp án',
  sequence_question: 'Câu sắp xếp', simulation: 'Mô phỏng', video_simulation: 'Video',
}
// Khối có ô NỘI DUNG (textarea) và khối có TIÊU ĐỀ riêng để sửa.
const EDITABLE_TEXT = new Set(['heading', 'text', 'concept_card', 'example', 'warning', 'conclusion_card', 'formula', 'formula_card'])
const TITLED = new Set(['concept_card', 'example', 'warning', 'conclusion_card', 'formula_card', 'table'])

// Sửa khối DANH SÁCH (mỗi dòng 1 mục) + kiểu chấm/số.
const ListEditor: React.FC<{ block: any; onChange: (p: any) => void }> = ({ block, onChange }) => {
  const items: string[] = Array.isArray(block.items) ? block.items : []
  return (
    <div className="mt-1.5 space-y-1">
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-bold text-gray-500">Kiểu</span>
        <select value={block.style || 'bullet'} onChange={(e) => onChange({ style: e.target.value })}
          className="text-[11px] border border-gray-200 rounded px-1.5 py-0.5 bg-white outline-none">
          <option value="bullet">• Chấm</option><option value="numbered">1. Số</option>
        </select>
      </div>
      {items.map((it, idx) => (
        <div key={idx} className="flex items-center gap-1.5">
          <span className="text-gray-300 text-xs w-4 text-right">{block.style === 'numbered' ? `${idx + 1}.` : '•'}</span>
          <input className="flex-1 p-1.5 border border-gray-200 rounded text-sm outline-none focus:ring-2 focus:ring-blue-200"
            value={it} onChange={(e) => { const n = [...items]; n[idx] = e.target.value; onChange({ items: n }) }} />
          <button onClick={() => onChange({ items: items.filter((_, k) => k !== idx) })} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      <button onClick={() => onChange({ items: [...items, ''] })} className="text-xs text-blue-600 font-semibold inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Thêm mục</button>
    </div>
  )
}

// Sửa khối BẢNG: lưới ô nhập đầy đủ + thêm/xoá hàng, cột.
const TableEditor: React.FC<{ block: any; onChange: (p: any) => void }> = ({ block, onChange }) => {
  const headers: string[] = Array.isArray(block.headers) ? block.headers : []
  const rows: string[][] = Array.isArray(block.rows) ? block.rows : []
  const norm = (r: any[]) => { const a = [...(r || [])]; while (a.length < headers.length) a.push(''); return a }
  const setHeader = (c: number, v: string) => { const h = [...headers]; h[c] = v; onChange({ headers: h }) }
  const setCell = (r: number, c: number, v: string) => { const rr = rows.map(norm); rr[r][c] = v; onChange({ rows: rr }) }
  const addCol = () => onChange({ headers: [...headers, ''], rows: rows.map((x) => [...norm(x), '']) })
  const delCol = (c: number) => onChange({ headers: headers.filter((_, k) => k !== c), rows: rows.map((x) => norm(x).filter((_, k) => k !== c)) })
  const addRow = () => onChange({ rows: [...rows, headers.map(() => '')] })
  const delRow = (r: number) => onChange({ rows: rows.filter((_, k) => k !== r) })
  const cell = 'w-28 p-1 border border-gray-200 rounded text-xs outline-none focus:ring-1 focus:ring-blue-200'
  return (
    <div className="mt-1.5 overflow-x-auto">
      <table className="border-collapse">
        <thead><tr>
          {headers.map((h, c) => (
            <th key={c} className="p-0.5 align-top">
              <input className={cell + ' font-bold bg-sky-50'} value={h} onChange={(e) => setHeader(c, e.target.value)} placeholder={`Cột ${c + 1}`} />
              <button onClick={() => delCol(c)} className="block text-[10px] text-red-400 hover:text-red-600 mt-0.5">✕ xoá cột</button>
            </th>
          ))}
          <th className="px-1 align-top"><button onClick={addCol} className="text-[11px] text-blue-600 font-bold">+ cột</button></th>
        </tr></thead>
        <tbody>
          {rows.map((row, r) => (
            <tr key={r}>
              {headers.map((_, c) => (
                <td key={c} className="p-0.5"><input className={cell} value={(row && row[c]) || ''} onChange={(e) => setCell(r, c, e.target.value)} /></td>
              ))}
              <td className="px-1"><button onClick={() => delRow(r)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <button onClick={addRow} className="text-xs text-blue-600 font-semibold inline-flex items-center gap-1 mt-1"><Plus className="w-3 h-3" /> Thêm hàng</button>
    </div>
  )
}

const Editor: React.FC<CardEditorProps> = ({ card, onChange }) => {
  const c = card as RichCard
  const items: any[] = Array.isArray(c.items) ? c.items : []
  const fileRef = useRef<HTMLInputElement>(null)
  const setItems = (next: any[]) => onChange({ items: next } as Partial<RichCard> as any)

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir; if (j < 0 || j >= items.length) return
    const n = [...items];[n[i], n[j]] = [n[j], n[i]]; setItems(n)
  }
  const del = (i: number) => setItems(items.filter((_, k) => k !== i))
  const patch = (i: number, p: any) => { const n = [...items]; n[i] = { ...n[i], ...p }; setItems(n) }
  const replace = (i: number, block: any) => { const n = [...items]; n[i] = block; setItems(n) }
  const addText = () => setItems([...items, { type: 'text', content: '' }])
  const addImageFiles = (files: FileList | null) => {
    if (!files?.length) return
    const reads = Array.from(files).map((f) => new Promise<any>((res) => {
      const r = new FileReader()
      r.onload = () => res({ type: 'image', src: r.result, content: r.result, alt: f.name })
      r.readAsDataURL(f)
    }))
    Promise.all(reads).then((imgs) => setItems([...items, ...imgs]))
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-gray-500 uppercase">Các khối nội dung ({items.length})</span>
        <div className="flex gap-1.5">
          <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 text-xs font-bold hover:bg-emerald-100"><ImagePlus className="w-3.5 h-3.5" /> Thêm ảnh</button>
          <button onClick={addText} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-100 text-slate-700 text-xs font-bold hover:bg-slate-200"><Plus className="w-3.5 h-3.5" /> Văn bản</button>
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => { addImageFiles(e.target.files); e.currentTarget.value = '' }} />
        </div>
      </div>

      {items.length === 0 && <p className="text-sm text-gray-400 p-3 text-center border border-dashed rounded-lg">Chưa có khối. Thêm ảnh / văn bản, hoặc import từ Word.</p>}

      <div className="space-y-1.5">
        {items.map((it, i) => (
          <div key={i} className="rounded-lg border border-gray-200 bg-white p-2">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 shrink-0">{TYPE_LABEL[it?.type] || it?.type}</span>
              {/* Chỉ hiện chữ mô tả cho khối KHÔNG có ô sửa bên dưới (tránh lặp tiêu đề/nội dung) */}
              {(EDITABLE_TEXT.has(it?.type) || TITLED.has(it?.type) || it?.type === 'list' || it?.type === 'table' || it?.type === 'image' || isQuestionBlock(it?.type))
                ? <span className="flex-1" />
                : <span className="text-[11px] text-gray-400 truncate flex-1">{it?.question || it?.title || (typeof it?.content === 'string' ? it.content.slice(0, 60) : '')}</span>}
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
              <button onClick={() => del(i)} className="p-1 text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>

            {/* Tiêu đề riêng của thẻ (Thẻ kiến thức/Ví dụ/Lưu ý/Tổng kết/Thẻ công thức/Bảng) */}
            {TITLED.has(it?.type) && (
              <input
                className="mt-1.5 w-full p-2 border border-gray-200 rounded-md text-sm font-semibold outline-none focus:ring-2 focus:ring-blue-200"
                value={it.title || ''} onChange={(e) => patch(i, { title: e.target.value })} placeholder="Tiêu đề (tuỳ chọn)" />
            )}

            {it?.type === 'image' && (it.src || it.content) && (
              <div className="mt-1.5">
                <img src={it.src || it.content} alt={it.alt || ''} className="max-h-56 rounded border border-gray-100 object-contain" />
                <input className="mt-1 w-full p-1.5 border border-gray-200 rounded text-xs outline-none focus:ring-2 focus:ring-blue-200"
                  value={it.alt || ''} onChange={(e) => patch(i, { alt: e.target.value })} placeholder="Mô tả ảnh (alt)" />
              </div>
            )}

            {EDITABLE_TEXT.has(it?.type) && (
              <textarea
                className="mt-1.5 w-full min-h-[88px] p-2 border border-gray-200 rounded-md text-sm leading-relaxed focus:ring-2 focus:ring-blue-200 outline-none resize-y"
                value={it.content || ''}
                onChange={(e) => patch(i, { content: e.target.value })}
                placeholder={it?.type === 'formula' || it?.type === 'formula_card' ? 'Công thức LaTeX (vd: v = v_0 + at)' : 'Nội dung (hỗ trợ **đậm**, $công thức$)'}
              />
            )}

            {it?.type === 'list' && <ListEditor block={it} onChange={(p) => patch(i, p)} />}
            {it?.type === 'table' && <TableEditor block={it} onChange={(p) => patch(i, p)} />}

            {isQuestionBlock(it?.type) && (
              <details className="mt-1.5" open>
                <summary className="text-xs text-blue-600 font-semibold cursor-pointer select-none">Sửa câu hỏi / đáp án</summary>
                <div className="mt-2"><QuestionEditor block={it} onChange={(nb) => replace(i, nb)} /></div>
              </details>
            )}

            {/* Khối chưa có ô sửa chuyên dụng (mô phỏng/video…) — hiện đầy đủ thông tin, không che */}
            {!EDITABLE_TEXT.has(it?.type) && !isQuestionBlock(it?.type) &&
              !['image', 'list', 'table'].includes(it?.type) && (
              <div className="mt-1.5 text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded p-2 space-y-1">
                {it?.note && <div><b>Ghi chú:</b> {it.note}</div>}
                {(it?.src || it?.url) && <div className="break-all"><b>Nguồn:</b> {String(it.src || it.url)}</div>}
                {!it?.note && !it?.src && !it?.url && <div>Khối "{TYPE_LABEL[it?.type] || it?.type}" — nội dung hiển thị ở bản xem trước bên phải.</div>}
              </div>
            )}
            {/* Lồng tiếng RIÊNG khối text (không áp dụng tiêu đề/câu hỏi) */}
            {isAudioableBlock(it) && (
              <BlockAudio block={it} cardId={c.id} blockIndex={i} onAudio={(p) => patch(i, p)} />
            )}
          </div>
        ))}
      </div>
      <p className="text-[11px] text-gray-400">Kéo thứ tự bằng nút ▲▼ (vd đưa ảnh về đúng chỗ). Sửa câu hỏi nâng cao sẽ bổ sung ở bước sau. Nhớ bấm Lưu bài.</p>
    </div>
  )
}

const Preview: React.FC<CardPreviewProps> = ({ card, onComplete }) => {
  const items = cardToContentItems(card)
  if (!items || items.length === 0) {
    return (
      <div className="p-4 rounded-lg bg-gray-50 border border-dashed border-gray-200 text-gray-400 text-sm text-center">
        Chưa có nội dung.
      </div>
    )
  }
  // Render bằng bộ render BAO TRÙM: khối trình bày + câu hỏi tương tác + mô phỏng.
  // onSectionComplete bắn khi HS trả lời hết câu hỏi chốt trong thẻ → tín hiệu "qua ải".
  // blockKeyPrefix = id thẻ → ghi "đã xem khối text" không trùng giữa các thẻ trong cùng phần.
  return <InteractiveContentRenderer content={items} onSectionComplete={onComplete} blockKeyPrefix={card.id} />
}

export const richCardDef: CardDef = {
  type: 'rich',
  label: 'Nội dung',
  description: 'Khối nội dung (sửa, sắp xếp, chèn ảnh)',
  icon: BookMarked,
  color: 'bg-sky-50 text-sky-600',
  createDefault: (): RichCard => ({ id: genId(), type: 'rich', items: [] }),
  Editor,
  Preview,
}
